import { IS_PROD } from './env';
import { PATHS } from './paths';
import AlphaAllowListIcon from './scenes/AlphaAllowList/AlphaAllowListIcon';
import AssistantIcon from './scenes/Assistant/AssistantIcon';
import BankConnectionsIcon from './scenes/BankConnections/BankConnectionsIcon';
import BillManagementIcon from './scenes/BillManagement/BillManagementIcon';
import DeleteAccountIcon from './scenes/DeleteAccount/DeleteAccountIcon';
import FeatureGatesIcon from './scenes/FeatureGates/FeatureGatesIcon';
import ImpersonateUserIcon from './scenes/ImpersonateUser/ImpersonateUserIcon';
import ObjectInspectorIcon from './scenes/ObjectInspector/ObjectInspectorIcon';
import ProcessorStatusesIcon from './scenes/ProcessorStatuses/ProcessorStatusesIcon';
import ReleasesIcon from './scenes/Releases/ReleasesIcon';
import TransactionAnomalyFeedbackIcon from './scenes/TransactionAnomalyFeedback/TransactionAnomalyFeedbackIcon';

import type * as React from 'react';

/**
 * Central registry of admin tools available in MOPS. A tool appears in two
 * places driven off this list:
 *
 * - The home scene renders a tile grid — one tile per registered tool.
 * - The Cmd+K command palette fuzzy-matches on `name` + `keywords` and
 *   navigates to `path` on Enter.
 *
 * Every admin-tool migration into mops-web (see `AGENTS.md` § "Migrating an
 * admin pane from @orn/web") must append an entry here with a custom SVG icon
 * colocated alongside the scene. No tool should exist in the router without an
 * entry here; otherwise it is reachable only by URL and operators cannot
 * discover it on the home page or via Cmd+K.
 */
export interface ITool {
  /** Stable identifier — used as React key and for telemetry. */
  id: string;
  /** Display name — sentence case, matches the tile label and palette row. */
  name: string;
  /** One-line description — shown on the tile and in the palette row. */
  description: string;
  /** Route path — must be a value from `PATHS`. */
  path: string;
  /**
   * Custom SVG icon rendered on the home tile. Conventionally colocated with
   * the scene at `src/scenes/<Tool>/<Tool>Icon.tsx`. Monoline, `currentColor`,
   * no fills — see `AlphaAllowListIcon` for the reference shape.
   */
  icon: React.ComponentType<{ className?: string }>;
  /** Extra search terms for the Cmd+K palette (aliases, old names, domain words). */
  keywords: string[];
  /**
   * Hide the tool in production — both the home tile and the Cmd+K palette
   * drop the entry when `IS_PROD`, so it's only discoverable in `dev` and
   * `staging`. The matching React Router route should be gated on `!IS_PROD`
   * in `MainApp` so the URL itself 404s in production. Mirrors the
   * `assertNotProd` server-side gate on the matching mops-api resolvers.
   */
  hideInProd?: boolean;
}

const ALL_TOOLS: ITool[] = [
  {
    id: 'alpha-allow-list',
    name: 'Alpha allow list',
    description: 'Add, search, and remove emails that bypass the closed-alpha gate.',
    path: PATHS.ALPHA_ALLOW_LIST,
    icon: AlphaAllowListIcon,
    keywords: ['alpha', 'allow', 'allowlist', 'access', 'beta', 'invite', 'email'],
  },
  {
    id: 'assistant',
    name: 'Assistant',
    description: "Inspect captured memories and walk a user's session history; hard-delete on request.",
    path: PATHS.ASSISTANT,
    icon: AssistantIcon,
    keywords: [
      'assistant',
      'memory',
      'memories',
      'observation',
      'observations',
      'recall',
      'ai',
      'chat',
      'session',
      'sessions',
      'thread',
      'threads',
      'transcript',
      'history',
    ],
  },
  {
    id: 'bank-connections',
    name: 'Bank connections',
    description: "Inspect a user's Plaid bank connections, force a resync, or revoke + delete.",
    path: PATHS.BANK_CONNECTIONS,
    icon: BankConnectionsIcon,
    keywords: ['bank', 'banks', 'connection', 'connections', 'plaid', 'resync', 'health', 'delete', 'revoke'],
  },
  {
    id: 'bill-management',
    name: 'Bill management',
    description: 'Browse, soft-delete, and seed bills and bill submissions for a selected user.',
    path: PATHS.BILL_MANAGEMENT,
    icon: BillManagementIcon,
    keywords: ['bill', 'bills', 'submission', 'submissions', 'invoice', 'payment', 'management'],
  },
  {
    id: 'delete-account',
    name: 'Delete account',
    description: 'Permanently delete a user — tombstone the account and purge their family in the background.',
    path: PATHS.DELETE_ACCOUNT,
    icon: DeleteAccountIcon,
    keywords: ['delete', 'account', 'remove', 'purge', 'user', 'gdpr', 'erase', 'wipe', 'offboard', 'close'],
  },
  {
    id: 'object-inspector',
    name: 'Object inspector',
    description: 'Look up any row by id and surface every related row across the schema, grouped by type.',
    path: PATHS.OBJECT_INSPECTOR,
    icon: ObjectInspectorIcon,
    keywords: ['object', 'inspector', 'inspect', 'id', 'uuid', 'row', 'lookup', 'debug', 'related'],
  },
  {
    id: 'impersonate-user',
    name: 'Impersonate user',
    description: 'Hand off into a user-product session as the selected user via a one-shot token.',
    path: PATHS.IMPERSONATE_USER,
    icon: ImpersonateUserIcon,
    keywords: ['impersonate', 'login', 'as', 'user', 'switch', 'session', 'support', 'debug', 'masquerade'],
  },
  {
    id: 'transaction-anomaly-feedback',
    name: 'Transaction anomaly feedback',
    description: 'Triage transactions flagged by the anomaly detector and label them P0–P4.',
    path: PATHS.TRANSACTION_ANOMALY_FEEDBACK,
    icon: TransactionAnomalyFeedbackIcon,
    keywords: ['transaction', 'anomaly', 'feedback', 'review', 'priority', 'callout', 'unusual', 'suspicious'],
  },
  {
    id: 'processor-statuses',
    name: 'Processor statuses',
    description: 'Inspect Plaid processor runs, retry failures, and resolve stuck webhooks.',
    path: PATHS.PROCESSOR_STATUSES,
    icon: ProcessorStatusesIcon,
    keywords: ['processor', 'webhook', 'plaid', 'sync', 'status', 'queue', 'job', 'failure', 'retry'],
  },
  {
    id: 'feature-gates',
    name: 'Feature gates',
    description: 'Manage feature gate rollouts, kill switches, and audience visibility.',
    path: PATHS.FEATURE_GATES,
    icon: FeatureGatesIcon,
    keywords: ['flag', 'flags', 'gate', 'gates', 'rollout', 'gating', 'kill switch', 'feature flag'],
  },
  {
    id: 'releases',
    name: 'Releases',
    description: 'Preview the next release, announce it on Slack with a GitHub draft, and review history.',
    path: PATHS.RELEASES,
    icon: ReleasesIcon,
    keywords: ['release', 'releases', 'tag', 'deploy', 'ship', 'version', 'changelog', 'announce'],
    hideInProd: true,
  },
];

/**
 * Tools available in the current environment. Tools flagged `hideInProd` are
 * dropped from the home tile grid and Cmd+K palette in production — the
 * matching React Router route is also gated on `!IS_PROD` in `MainApp`.
 */
export const TOOLS: ITool[] = ALL_TOOLS.filter((tool) => !tool.hideInProd || !IS_PROD);
